// -----------------------------------------------------------------------------
// Library
// -----------------------------------------------------------------------------
import React, { useCallback, useState } from "react";

// -----------------------------------------------------------------------------
// Components
// -----------------------------------------------------------------------------
import { Button } from "components/Common/Button";
import { Heading3 } from "themes/default/_typography";
import { DiamondOutlinedIcon, PersonPlusIcon, GymIcon, DiamondIcon } from "components/Icons";
import { AccountCard } from "components/Common/Cards/AccountCard";
import ProfileBanner from "components/UI/ProfileBanner";
import { Modal, Typography, List, Flex } from "antd";
import UpdatePassword from "pages/Dashboard/Header/UpdatePassword";
import EditProfile from "components/EditProfile";
import usePrimaryColor from "helpers/hooks/usePrimaryColor";
import { PhoneNumberAlert } from "components/Alerts";
import { SearchOutlined } from "@ant-design/icons";
// -----------------------------------------------------------------------------
// Styles and helpers
// -----------------------------------------------------------------------------
import useLogout from "utils/useLogout";
import StyledProfile from "./styles";
import { useNavigate, useParams } from "react-router";

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
const Profile = () => {
	const { gym } = useParams();
	const navigate = useNavigate();
	const primaryColor = usePrimaryColor();
	const [openResetPassword, setOpenResetPassword] = useState(false);
	const [openEditProfile, setOpenEditProfile] = useState(false);
	const logoutHook = useLogout(navigate, gym);

	const handleLogout = useCallback(() => {
		return logoutHook.logout();
	}, [logoutHook]);

	const handleAccountSettingsClick = useCallback(() => {
		navigate(`../account-settings`);
	}, [navigate]);

	const handleGymClick = useCallback(() => {
		navigate(`../my-gym`);
	}, [navigate]);

	const handleMembershipClick = useCallback(() => {
		navigate(`../consumer-membership`);
	}, [navigate]);

	const dataSource = [
		{
			title: "Account Settings",
			icon: (
				<Flex justify="center" align="center" className="sb-icon">
					<SearchOutlined className="search icon" />
				</Flex>
			),
			text: "Manage profile information",
			name: "accountSettings",
			onClick: handleAccountSettingsClick,
		},
		{
			title: "Change Email/Phone Number",
			icon: (
				<Flex justify="center" align="center" className="sb-icon">
					<DiamondIcon />
				</Flex>
			),
			text: "Change your email or phone number",
			name: "emailPhone",
			onClick: () => setOpenEditProfile(true),
		},
		{
			title: "Reset Password",
			icon: <PersonPlusIcon />,
			text: "Update your password",
			name: "resetPassword",
			onClick: () => setOpenResetPassword(true),
		},
		{
			title: "My Gym",
			icon: (
				<Flex justify="center" align="center" className="sb-icon">
					<GymIcon className="icon" />
				</Flex>
			),
			text: "About your gym, address, gym philosophy",
			name: "myGym",
			onClick: handleGymClick,
		},
		{
			title: "Membership",
			icon: (
				<Flex justify="center" align="center" className="sb-icon">
					<DiamondOutlinedIcon className="sb-icon" />
				</Flex>
			),
			text: "Edit payment information, payment dates, receipts",
			name: "membership",
			onClick: handleMembershipClick,
		},
	];
	return (
		<>
			<Modal
				open={openEditProfile}
				footer={null}
				title={<Typography.Title level={3}>Edit Profile</Typography.Title>}
				onCancel={() => setOpenEditProfile(false)}
				destroyOnClose
				maskClosable={false}
			>
				<EditProfile onCancel={() => setOpenEditProfile(false)} />
			</Modal>
			<StyledProfile>
				<div className="sb-profile-container">
					<ProfileBanner title="My Profile" picture={true} />
					<div className="sb-profile-container__body">
						<Heading3 className="sb-profile-container__body__title">Account</Heading3>
						<PhoneNumberAlert />
						<Flex justify="center" align="center" style={{ flexDirection: "column" }} gap={16}>
							<List
								dataSource={dataSource}
								renderItem={(item, index) => (
									<AccountCard
										onClick={item.onClick}
										customColor={false}
										icon={item.icon}
										header={item.title}
										text={item.text}
										name={item.name}
									/>
								)}
							/>
							<Flex className="action-area" style={{ width: "100%", maxWidth: 800 }} wrap>
								<Flex flex={1} style={{ width: "100%", maxWidth: 800 }} wrap justify="space-between" gap={16}>
									<Button
										style={{ flex: 1, whiteSpace: "nowrap", minWidth: "unset", maxWidth: "unset" }}
										className="primary-filled"
										onClick={handleLogout}
										variant="contained"
										data-id="btnLogout"
									>
										Log out
									</Button>
								</Flex>
							</Flex>
						</Flex>
					</div>
				</div>

				<Modal
					title={<Typography.Title level={3}>Update Password</Typography.Title>}
					open={openResetPassword}
					onCancel={() => setOpenResetPassword(false)}
					maskClosable={false}
					closable
					style={{ "--primary-color": primaryColor }}
					footer={[]}
					destroyOnClose
				>
					<UpdatePassword open={openResetPassword} onCancel={() => setOpenResetPassword(false)} />
				</Modal>
			</StyledProfile>
		</>
	);
};

export default Profile;
